// Minimal no-dependency drag-to-reorder for the report's Customize mode.
//
// The grip is the drag source (`gripProps`); each slot is a drop zone
// (`zoneProps`). On drop, `move(from, to)` is called so the caller can reorder
// its order array (e.g. via the layout store). `overIndex` is the slot the
// pointer is currently over — use it to paint a drop indicator.
import { useRef, useState, type DragEvent } from 'react'

export interface DragReorder {
	overIndex: number | null
	dragIndex: number | null
	/** Index a card just landed on — drives a one-shot "settle" pop. Auto-clears. */
	justMoved: number | null
	gripProps: (index: number) => {
		draggable: true
		onDragStart: (e: DragEvent) => void
		onDragEnd: () => void
	}
	zoneProps: (index: number) => {
		onDragOver: (e: DragEvent) => void
		onDragEnter: (e: DragEvent) => void
		onDrop: (e: DragEvent) => void
	}
}

export const useDragReorder = (move: (from: number, to: number) => void): DragReorder => {
	const [dragIndex, setDragIndex] = useState<number | null>(null)
	const [overIndex, setOverIndex] = useState<number | null>(null)
	const [justMoved, setJustMoved] = useState<number | null>(null)
	const popTimer = useRef<ReturnType<typeof setTimeout>>()
	// Teardown for the live drag clone (removes the ghost + its dragover listener).
	const ghostCleanup = useRef<(() => void) | null>(null)

	// Flag the destination slot so it can play the settle pop, then clear it so
	// the animation can replay on the next drop (a persistent class wouldn't).
	const flashMoved = (to: number) => {
		clearTimeout(popTimer.current)
		setJustMoved(to)
		popTimer.current = setTimeout(() => setJustMoved(null), 360)
	}

	return {
		overIndex,
		dragIndex,
		justMoved,
		gripProps: (index) => ({
			draggable: true,
			onDragStart: (e) => {
				setDragIndex(index)
				e.dataTransfer.effectAllowed = 'move'
				// Firefox requires data to be set for a drag to start.
				e.dataTransfer.setData('text/plain', String(index))
				// The browser's built-in drag image is a one-shot raster snapshot, and
				// its compositor drops the chart's inline SVG (offscreen OR on-screen) —
				// the ghost came out blank below the title. So we SUPPRESS that snapshot
				// (a 1×1 transparent image) and instead drag a real, live clone that
				// follows the pointer: a rendered DOM node paints the chart exactly like
				// the source. Uniform 12px corners so it reads as a standalone card
				// regardless of slot. The live card in the row is untouched.
				// The clone hangs off <body>, under the themed <html>, so theme tokens
				// still resolve — paint it with the card SURFACE + BORDER tokens (as the
				// table's column ghost does), never a literal white: that stayed light
				// in dark mode while the cloned text went dark (Esteban, 2026-09-03).
				const el = e.currentTarget as HTMLElement
				const rect = el.getBoundingClientRect()
				const blank = new Image()
				blank.src =
					'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'
				e.dataTransfer.setDragImage(blank, 0, 0)
				const ghost = el.cloneNode(true) as HTMLElement
				const offsetX = e.clientX - rect.left
				const offsetY = e.clientY - rect.top
				Object.assign(ghost.style, {
					width: `${rect.width}px`,
					height: `${rect.height}px`,
					position: 'fixed',
					top: `${rect.top}px`,
					left: `${rect.left}px`,
					margin: '0',
					borderRadius: '12px',
					overflow: 'hidden',
					background: 'rgb(var(--cg-surface))',
					boxShadow: '0 0 0 1px rgb(var(--cg-border)), 0 12px 28px rgba(0,0,0,0.22)',
					pointerEvents: 'none',
					zIndex: '2147483647',
					opacity: '0.96'
				})
				document.body.appendChild(ghost)
				// Native drag fires `dragover` across the document with live pointer
				// coords; move the clone to follow (ignore the bogus 0,0 it emits at the
				// very end of the gesture).
				// Native DOM DragEvent — the imported `DragEvent` is React's synthetic type.
				const follow = (ev: globalThis.DragEvent) => {
					if (ev.clientX === 0 && ev.clientY === 0) return
					ghost.style.left = `${ev.clientX - offsetX}px`
					ghost.style.top = `${ev.clientY - offsetY}px`
				}
				document.addEventListener('dragover', follow)
				ghostCleanup.current = () => {
					document.removeEventListener('dragover', follow)
					ghost.remove()
					ghostCleanup.current = null
				}
			},
			onDragEnd: () => {
				setDragIndex(null)
				setOverIndex(null)
				ghostCleanup.current?.()
			}
		}),
		zoneProps: (index) => ({
			onDragOver: (e) => {
				// Only react while a reorder drag is in flight.
				if (dragIndex === null) return
				e.preventDefault()
				e.dataTransfer.dropEffect = 'move'
			},
			onDragEnter: (e) => {
				if (dragIndex === null) return
				e.preventDefault()
				setOverIndex(index)
			},
			onDrop: (e) => {
				if (dragIndex === null) return
				e.preventDefault()
				if (dragIndex !== index) {
					move(dragIndex, index)
					flashMoved(index)
				}
				setDragIndex(null)
				setOverIndex(null)
			}
		})
	}
}
